import { deleteCookie } from "cookies-next";

import { integradorSessionKeys } from "src/hooks/integrador/useClientIntegrador";
import type { TeamType } from "src/hooks/teams/props";
import {
  clearCurrentTeam,
  clearStoredTeams,
  persistCurrentTeam,
  persistStoredTeams,
} from "src/hooks/teams/storage";
import { syncSessionKeys } from "src/hooks/useClientSync";
import {
  clearAndResetSyncSession,
  persistAndPublishSyncSession,
} from "src/hooks/integrador/useSyncSession/service";
import { createKeyedSingleFlight } from "src/lib/session/single-flight";
import { resetPlatformsSessionState } from "src/lib/platforms/session-browser";
import { selectCurrentTeam } from "./switch-tenant";

type ProductSessions = {
  integradorSession?: any;
  integradorUnavailable?: boolean;
  syncSession?: any;
};

type OtpRequestResponse =
  | {
      authenticated: false;
      challengeId: string;
      channel: "email" | "whatsapp";
      expiresInSeconds: number;
    }
  | ({ authenticated: true } & ProductSessions);

const syncTokenCookie = "app-sync-auth-token";
const integradorTokenCookie = "flowbits-integrador-auth-token";
const tenantSwitches = createKeyedSingleFlight<string, ProductSessions>();

export async function requestCentralOtp(input: {
  email?: string;
  phoneNumber?: string;
  preferredChannel: "email" | "whatsapp";
}) {
  const response = await fetch("/api/auth-central/request-otp", {
    body: JSON.stringify(input),
    headers: { "content-type": "application/json" },
    method: "POST",
  });

  return parseJsonResponse<OtpRequestResponse>(response);
}

export async function verifyCentralOtp(input: {
  challengeId: string;
  code: string;
}) {
  clearProductSessions();
  const response = await fetch("/api/auth-central/verify-otp", {
    body: JSON.stringify(input),
    headers: { "content-type": "application/json" },
    method: "POST",
  });
  const sessions = await parseJsonResponse<ProductSessions>(response);
  persistProductSessions(sessions);
  return sessions;
}

export async function acceptWorkspaceInviteOtp(input: {
  challengeId: string;
  code: string;
  token: string;
}) {
  const response = await fetch("/api/workspace-invites/accept", {
    body: JSON.stringify(input),
    headers: { "content-type": "application/json" },
    method: "POST",
    referrerPolicy: "no-referrer",
  });
  const result = await parseJsonResponse<
    ProductSessions & { workspaceId: number }
  >(response);
  persistProductSessions(result, { currentTeamId: String(result.workspaceId) });
  return result;
}

export async function switchCentralProductTenant(
  input: { teamId: string },
  options: { signal?: AbortSignal; experience?: "redesign" } = {},
) {
  const switchKey = input.teamId;

  const execute = async () => {
    options.signal?.throwIfAborted();
    const response = await fetch("/api/auth-central/switch-integrador-tenant", {
      body: JSON.stringify(
        options.experience
          ? { ...input, experience: options.experience }
          : input,
      ),
      headers: { "content-type": "application/json" },
      method: "POST",
      signal: options.signal,
    });
    const sessions = await parseJsonResponse<ProductSessions>(response);
    options.signal?.throwIfAborted();
    await clearIntegradorTeamStores(options.signal);
    options.signal?.throwIfAborted();
    persistProductSessions(sessions, { currentTeamId: input.teamId });
    return sessions;
  };
  // A cancellable caller owns its request; it cannot share another caller's signal.
  return options.signal ? execute() : tenantSwitches.run(switchKey, execute);
}

export function persistProductSessions(
  sessions: ProductSessions,
  options: { currentTeamId?: string } = {},
) {
  const syncResult = sessions.syncSession?.result;
  if (!syncResult) {
    throw new Error("Sync session is missing from authentication response");
  }

  clearPersistedProductSessionData();
  persistAndPublishSyncSession(syncResult);
  persistSyncTeams(syncResult.teamsInformation ?? [], options.currentTeamId);

  const integradorResult = sessions.integradorSession?.result;
  if (integradorResult) {
    localStorage.setItem(
      integradorSessionKeys.user,
      JSON.stringify(integradorResult.user),
    );
    localStorage.setItem(
      integradorSessionKeys.tenantId,
      String(integradorResult.tenant?.id ?? integradorResult.tenantId ?? ""),
    );
    localStorage.setItem(
      integradorSessionKeys.userPermission,
      JSON.stringify(integradorResult.permissions),
    );
    localStorage.setItem(
      integradorSessionKeys.userVendor,
      JSON.stringify(integradorResult.userLinkedVendors ?? []),
    );
    localStorage.setItem(
      integradorSessionKeys.grantedPermissions,
      JSON.stringify(integradorResult.grantedPermissions ?? []),
    );
  }
}

export function clearProductSessions() {
  resetPlatformsSessionState();
  clearPersistedProductSessionData();
  clearAndResetSyncSession();

  deleteCookie(syncTokenCookie);
  deleteCookie(integradorTokenCookie);
}

function clearPersistedProductSessionData() {
  localStorage.removeItem(syncSessionKeys.user);
  localStorage.removeItem(syncSessionKeys.userPermission);
  localStorage.removeItem(syncSessionKeys.userApps);
  localStorage.removeItem(syncSessionKeys.grantedPermissions);
  localStorage.removeItem(integradorSessionKeys.user);
  localStorage.removeItem(integradorSessionKeys.tenantId);
  localStorage.removeItem(integradorSessionKeys.userPermission);
  localStorage.removeItem(integradorSessionKeys.userVendor);
  localStorage.removeItem(integradorSessionKeys.grantedPermissions);
  clearStoredTeams();

  clearCurrentTeam();
}

function persistSyncTeams(
  teamsInformation: TeamType[],
  currentTeamId?: string,
) {
  if (!Array.isArray(teamsInformation)) {
    return;
  }

  persistStoredTeams(teamsInformation);

  const currentTeam = selectCurrentTeam(teamsInformation, currentTeamId);
  if (currentTeam) {
    persistCurrentTeam(currentTeam);
  }
}

async function clearIntegradorTeamStores(signal?: AbortSignal) {
  try {
    const [inventoryFilter, vendor, integradorConfig] = await Promise.all([
      import("src/store/inventoryFilterStore"),
      import("src/store/vendorStore"),
      import("src/store/integradorConfig"),
    ]);

    signal?.throwIfAborted();
    inventoryFilter.useInventoryFilterStore.getState().clearFilters();
    vendor.useVendorStore.getState().setVendor(null);
    vendor.useVendorStore.getState().setAddress(null);
    integradorConfig.useIntegradorConfigStore
      .getState()
      .setDefaultVendorFields(null);
    integradorConfig.useIntegradorConfigStore
      .getState()
      .setSelectedCarImages([]);
    integradorConfig.useIntegradorConfigStore
      .getState()
      .setSelectedMotorcycleImages([]);
  } catch (error) {
    if (signal?.aborted) throw error;
    console.warn("[auth-central] Failed to clear Integrador team state", error);
  }
}

async function parseJsonResponse<T = any>(response: Response): Promise<T> {
  const payload = await response.json();
  if (!response.ok) {
    throw new Error(payload.message ?? "Authentication request failed");
  }

  return payload as T;
}
